import React, { createContext, useContext, useState, useEffect } from 'react';
import { persistImpersonatedArtistIdFromUrlOrCookie } from './impersonationContext';

/** JWT from admin impersonation: `?token=...` — must be read here because Apollo uses this module, not URL-aware hooks. */
export function readAuthTokenFromLocation(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = new URLSearchParams(window.location.search).get('token');
    if (!raw || raw === 'undefined') return null;
    const decoded = decodeURIComponent(raw).trim();
    if (!decoded || decoded === 'undefined') return null;
    return decoded.startsWith('Bearer ') ? decoded.slice(7).trim() : decoded;
  } catch {
    return null;
  }
}

/** One-hop role flags from LoginPage cross-subdomain redirect (`?isAdmin=1&isArtist=0`). */
function applyRoleFlagsFromSearchParams(): void {
  try {
    const p = new URLSearchParams(window.location.search);
    const admin = p.get('isAdmin');
    const artist = p.get('isArtist');
    if (admin === '1' || admin === 'true') localStorage.setItem('isAdmin', 'true');
    if (admin === '0' || admin === 'false') localStorage.setItem('isAdmin', 'false');
    if (artist === '1' || artist === 'true') localStorage.setItem('isArtist', 'true');
    if (artist === '0' || artist === 'false') localStorage.setItem('isArtist', 'false');
  } catch {
    // ignore
  }
}

function stripAuthHandoffParamsFromUrl(): void {
  try {
    const u = new URL(window.location.href);
    if (
      !u.searchParams.has('token') &&
      !u.searchParams.has('isAdmin') &&
      !u.searchParams.has('isArtist')
    ) {
      return;
    }
    u.searchParams.delete('token');
    u.searchParams.delete('isAdmin');
    u.searchParams.delete('isArtist');
    const qs = u.searchParams.toString();
    window.history.replaceState({}, '', `${u.pathname}${qs ? `?${qs}` : ''}${u.hash}`);
  } catch {
    // ignore
  }
}

function normalizeAuthToken(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") return null;
  return trimmed.startsWith("Bearer ") ? trimmed.slice(7).trim() : trimmed;
}

/** Unverified decode of JWT payload (same shape as backend `JWTPayload`: role, email, …). */
function decodeJwtPayload(token: string | null): { role?: string } | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64);
    const payload = JSON.parse(json) as { role?: string };
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

function roleFromStoredUser(): string | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw) as { role?: string };
    return typeof u?.role === 'string' ? u.role.toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * Resolve portal roles for ArtistFlowHandler, login redirect, and layout.
 * Order: login `localStorage` flags, then JWT `role`, then cached `user` JSON from registration.
 */
export function resolveSessionRoles(activeToken: string | null): {
  isArtist: boolean;
  isBeneficiary: boolean;
} {
  const lsArtist = localStorage.getItem('isArtist') === 'true';
  const lsBeneficiary = localStorage.getItem('isBeneficiary') === 'true';

  const payload = decodeJwtPayload(activeToken);
  const jwtRole = (payload?.role || '').toLowerCase();
  const userRole = roleFromStoredUser() || '';
  const role = jwtRole || userRole;

  const isArtist = lsArtist || role === 'artist';
  const isBeneficiary = lsBeneficiary || role === 'user';

  return { isArtist, isBeneficiary };
}

interface TokenManagerContextType {
  getToken: () => string | null;
  setToken: (token: string | null) => void;
  storeToken: (token: string | null) => void;
  clearToken: () => void;
  isArtist: boolean;
  isBeneficiary: boolean;
}

const TokenManagerContext = createContext<TokenManagerContextType | undefined>(undefined);

export const TokenManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Run before the first child render so `/dashboard?artistId=...` + admin JWT does not briefly
  // (or stuck without re-render) resolve as the admin shell — useEffect runs too late for routing.
  if (typeof window !== 'undefined') {
    persistImpersonatedArtistIdFromUrlOrCookie();
  }

  const [token, setTokenState] = useState<string | null>(null);

  useEffect(() => {
    // Admin impersonation: artistId in URL/cookie must land in *this* origin's localStorage
    // (admin.starfarm.com and artist.starfarm.com do not share localStorage).
    persistImpersonatedArtistIdFromUrlOrCookie();

    // Prefer token from URL (impersonation) then localStorage
    const urlToken = readAuthTokenFromLocation();
    if (urlToken) {
      localStorage.setItem('token', urlToken);
      setTokenState(urlToken);
      applyRoleFlagsFromSearchParams();
      stripAuthHandoffParamsFromUrl();
      return;
    }
    const storedToken = normalizeAuthToken(localStorage.getItem('token'));
    if (storedToken) {
      localStorage.setItem('token', storedToken);
      setTokenState(storedToken);
    }
  }, []);

  const getToken = (): string | null => {
    const urlToken = readAuthTokenFromLocation();
    if (urlToken) return urlToken;
    return normalizeAuthToken(token || localStorage.getItem('token'));
  };

  const setToken = (newToken: string | null): void => {
    setTokenState(newToken);
  };

  const storeToken = (newToken: string | null): void => {
    const normalized = normalizeAuthToken(newToken);
    if (normalized) {
      localStorage.setItem('token', normalized);
      setTokenState(normalized);
    } else {
      localStorage.removeItem('token');
      setTokenState(null);
    }
  };

  const clearToken = (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('isArtist');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('isBeneficiary');
    localStorage.removeItem('isStreamer');
    setTokenState(null);
  };

  // Always re-resolve from getToken() + localStorage so post-login / register paths
  // (which may only write localStorage) still see correct roles before state catches up.
  const { isArtist, isBeneficiary } = resolveSessionRoles(getToken());

  const value: TokenManagerContextType = {
    getToken,
    setToken,
    storeToken,
    clearToken,
    isArtist,
    isBeneficiary,
  };

  return (
    <TokenManagerContext.Provider value={value}>
      {children}
    </TokenManagerContext.Provider>
  );
};

export const useTokenManager = (): TokenManagerContextType => {
  const context = useContext(TokenManagerContext);
  if (context === undefined) {
    throw new Error('useTokenManager must be used within a TokenManagerProvider');
  }
  return context;
};

// Default export for compatibility (used by src/providers/ApolloProvider.tsx)
const TokenManager = {
  getToken: (): string | null => {
    const urlToken = readAuthTokenFromLocation();
    if (urlToken) return urlToken;
    return normalizeAuthToken(localStorage.getItem('token'));
  },
  setToken: (token: string | null) => {
    const normalized = normalizeAuthToken(token);
    if (normalized) {
      localStorage.setItem('token', normalized);
    } else {
      localStorage.removeItem('token');
    }
  },
  storeToken: (token: string | null) => {
    const normalized = normalizeAuthToken(token);
    if (normalized) {
      localStorage.setItem('token', normalized);
    } else {
      localStorage.removeItem('token');
    }
  },
  clearToken: () => localStorage.removeItem('token'),
};

export default TokenManager;
